import React, {useMemo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip,
} from 'chart.js';
import {Bar, Line, Pie} from 'react-chartjs-2';
import {StepTable} from '../../../@types/agent-investigation';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, LineElement, PointElement,
    Tooltip, Legend);

/** Beyond this a chart is unreadable, so the table stays the only view. */
const MOST_POINTS_WORTH_PLOTTING = 60;

/** Shared blue-first ordering with the dashboards and the chat charts. */
const PALETTE = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#64748b'];

/** A plottable reading of a step's table: one series per numeric column. */
export type StepChartKind = 'line' | 'bar' | 'pie';

export interface StepChartData {
    /** How the agent asked for it to be shown, corrected to something the data can support. */
    kind: StepChartKind;
    labels: string[];
    series: {label: string; values: number[]}[];
}

/**
 * A step's table read as a chart, or null when it is not the shape of one.
 *
 * <p>The numbers are taken from the table and never from the model: the agent is forbidden from
 * doing arithmetic so that no figure in an answer is its own work, and a chart it authored would put
 * numbers it had transcribed in the most authoritative-looking place on the screen. Which kind of
 * chart to draw is a different question, and that judgement is the agent's, since it is the one that
 * knows what was asked.</p>
 *
 * <p>Its choice is still corrected to something the data can carry: a pie divides one whole, so a
 * cross-tabulation with a series per outcome is drawn as bars instead of silently showing one
 * series.</p>
 */
export const chartFor = (table: StepTable): StepChartData | null => {
    if (table.columns.length < 2 || table.rows.length < 2 || table.rows.length > MOST_POINTS_WORTH_PLOTTING) {
        return null;
    }
    const numeric: number[] = [];
    for (let column = 1; column < table.columns.length; column++) {
        if (table.rows.every((row) => Number.isFinite(Number(row[column])) && row[column] !== '')) {
            numeric.push(column);
        }
    }
    if (numeric.length === 0) {
        return null;
    }
    const series = numeric.map((column) => ({
        label: table.columns[column],
        values: table.rows.map((row) => Number(row[column])),
    }));
    const key = table.columns[0];
    const fallback: StepChartKind = key === 'decade' || key === 'year' ? 'line' : 'bar';
    const asked = (table.chart ?? '').toLowerCase();
    const wanted: StepChartKind = asked === 'pie' || asked === 'line' || asked === 'bar' ? asked : fallback;
    return {
        // A pie divides one whole, which several series do not.
        kind: wanted === 'pie' && series.length > 1 ? 'bar' : wanted,
        labels: table.rows.map((row) => row[0]),
        series,
    };
};

export interface StepChartProps {
    data: StepChartData;
    title?: string;
}

/** Draws what {@link chartFor} read out of a step's table. */
export const StepChart: React.FC<StepChartProps> = ({data, title}) => {
    const chart = useMemo(() => ({
        labels: data.labels,
        datasets: data.series.map((series, index) => ({
            label: series.label,
            data: series.values,
            // A pie colours its segments rather than its series, so the palette runs across the rows.
            backgroundColor: data.kind === 'pie'
                ? series.values.map((unused, row) => PALETTE[row % PALETTE.length])
                : PALETTE[index % PALETTE.length],
            borderColor: data.kind === 'pie' ? '#fff' : PALETTE[index % PALETTE.length],
            borderWidth: data.kind === 'line' ? 2 : (data.kind === 'pie' ? 1 : 0),
            pointRadius: data.kind === 'line' ? 2 : 0,
            tension: 0.25,
        })),
    }), [data]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            // A pie needs its key, because the labels are the segments. A single line or bar series
            // does not: its name is already above the chart.
            legend: {display: data.kind === 'pie' || data.series.length > 1, position: 'bottom' as const,
                labels: {boxWidth: 10, font: {size: 10}}},
        },
        // A pie has no axes to scale.
        ...(data.kind === 'pie' ? {} : {
            scales: {
                x: {ticks: {font: {size: 10}}, grid: {display: false}},
                y: {beginAtZero: true, ticks: {font: {size: 10}, precision: 0}},
            },
        }),
    }), [data.kind, data.series.length]);

    return (
        <Box sx={{my: 0.5}}>
            {title && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{mb: 0.5}}>
                    {title}
                </Typography>
            )}
            <Box sx={{height: 220}}>
                {data.kind === 'line' && <Line data={chart} options={options} />}
                {data.kind === 'bar' && <Bar data={chart} options={options} />}
                {data.kind === 'pie' && <Pie data={chart} options={options} />}
            </Box>
        </Box>
    );
};

export default StepChart;
